import React, { useCallback, useEffect, useState } from 'react';
import { Container, LoginButton } from '../../styles/Styles';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import { GET_TOURS_LIST } from '../gql/tours-gql';
import { CONFIRM_TOUR_BY_GUIDE } from '../gql/tours-gql';
import { useAuth } from '../../context/AuthContext';
import { Roles } from '../common/roles';
import { Navigate } from 'react-router-dom';
import moment from 'moment/moment';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import Loader from '../common/Loader';
import GuideNav from './GuideNav';
import TourCalendar from '../common/TourCalendar';

const TourCard = styled.div`
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    margin-bottom: 1rem;
`;

const TourHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
`;

const TourTitle = styled.h3`
    margin: 0;
    color: #333;
    font-size: 1.2rem;
`;

const TourDetails = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
`;

const DetailLabel = styled.span`
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.25rem;
`;

const DetailValue = styled.span`
    font-weight: 500;
    color: #333;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 1rem;
    margin-top: 1rem;
`;

const ConfirmButton = styled(LoginButton)<{ $confirmed?: boolean }>`
    background-color: ${props => props.$confirmed ? '#4CAF50' : '#f44336'};
    &:hover {
        background-color: ${props => props.$confirmed ? '#45a049' : '#da190b'};
    }
`;

const PendingToursSection = styled.div`
    margin-top: 2rem;
    padding: 1rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PENDING_MONTH_LOOKBACK = 12;
const PENDING_MONTH_LOOKAHEAD = 12;

const getGuideConfirmation = (tour: any, guideId?: string) => {
    const guideEntry = tour.guide?.find((g: any) => g._id === guideId);
    if (guideEntry) {
        return {
            isConfirmedByGuide: guideEntry.isConfirmedByGuide,
            isConfirmedDate: guideEntry.isConfirmedDate
        };
    }
    return {
        isConfirmedByGuide: tour.isConfirmedByGuide,
        isConfirmedDate: tour.isConfirmedDate
    };
};

const GuideDashboard = () => {
    const { user, isAuthenticated } = useAuth();
    const apolloClient = useApolloClient();

    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [pendingTours, setPendingTours] = useState<any[]>([]);
    const [loadingPendingTours, setLoadingPendingTours] = useState(false);
    const toursQueryVars = {
        month: currentMonth.getMonth() + 1,
        year: currentMonth.getFullYear()
    };

    const { data: toursList, loading } = useQuery(GET_TOURS_LIST, {
        variables: toursQueryVars
    });
    const tours = toursList?.getToursList || [];

    // Filter tours to only show ones assigned to the current guide
    const guideTours = tours.filter((tour: any) =>
        tour.guide?.some((g: any) => g._id === user?.id) || !tour.guide
    );

    const guideToursForCalendar = guideTours.map((tour: any) => {
        const confirmation = getGuideConfirmation(tour, user?.id);
        return {
            ...tour,
            isConfirmedByGuide: confirmation.isConfirmedByGuide,
            isConfirmedDate: confirmation.isConfirmedDate
        };
    });

    const loadPendingTours = useCallback(async () => {
        if (!user?.id) return;

        setLoadingPendingTours(true);

        try {
            const monthOffsets = Array.from(
                { length: PENDING_MONTH_LOOKBACK + PENDING_MONTH_LOOKAHEAD + 1 },
                (_, index) => index - PENDING_MONTH_LOOKBACK
            );

            const monthQueries = monthOffsets.map((offset) => {
                const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
                return apolloClient.query({
                    query: GET_TOURS_LIST,
                    variables: {
                        month: date.getMonth() + 1,
                        year: date.getFullYear()
                    }
                });
            });

            const monthResults = await Promise.all(monthQueries);
            const mergedTours = monthResults.flatMap((result) => result.data?.getToursList || []);

            const seen = new Set<string>();
            const pendingAcrossMonths = mergedTours.filter((tour: any) => {
                if (seen.has(tour._id)) return false;
                seen.add(tour._id);

                const assignedToCurrentGuide = tour.guide?.some((g: any) => g._id === user.id) || !tour.guide;
                const confirmation = getGuideConfirmation(tour, user.id);
                return assignedToCurrentGuide && confirmation.isConfirmedByGuide === false;
            });

            pendingAcrossMonths.sort(
                (a: any, b: any) => new Date(a.bookingDate).getTime() - new Date(b.bookingDate).getTime()
            );

            setPendingTours(pendingAcrossMonths);
        } catch (error) {
            console.error("Failed to load pending tours across months:", error);
            toast.error("Failed to load pending tours");
        } finally {
            setLoadingPendingTours(false);
        }
    }, [apolloClient, currentMonth, user?.id]);

    useEffect(() => {
        loadPendingTours();
    }, [loadPendingTours]);

    const [confirmTour] = useMutation(CONFIRM_TOUR_BY_GUIDE, {
        onCompleted: async () => {
            toast.success("Tour status updated successfully!");
            await loadPendingTours();
        },
        onError: (error) => {
            toast.error("Failed to update tour status: " + error.message);
        },
        refetchQueries: [{
            query: GET_TOURS_LIST,
            variables: toursQueryVars
        }]
    });

    const handleConfirmTour = async (tourId: string, status: boolean, closeModal?: () => void) => {
        await confirmTour({
            variables: {
                tourConfirmationInput: {
                    tourId,
                    guideId: user?.id,
                    status
                }
            }
        });
        closeModal?.();
    };

    const renderTourCard = (tour: any, options?: { closeModal?: () => void }) => {
        const closeModal = options?.closeModal;
        const confirmation = getGuideConfirmation(tour, user?.id);
        return (
            <TourCard key={tour._id}>
                <TourHeader>
                    <TourTitle>{tour.title || tour.tourTitle}</TourTitle>
                </TourHeader>
                <TourDetails>
                    <DetailItem>
                        <DetailLabel>Date</DetailLabel>
                        <DetailValue>{moment(tour.bookingDate).format("D MMM YYYY h:mm a")}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Total Customers</DetailLabel>
                        <DetailValue>{tour.totalCustomers || 0}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Language</DetailLabel>
                        <DetailValue>{tour.language ?? 'Not set'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                        <DetailLabel>Confirmation Status</DetailLabel>
                        <DetailValue>
                            {confirmation.isConfirmedByGuide === true ? 'Confirmed' : 'Awaiting Approval'}
                            {confirmation.isConfirmedDate && ` (${moment(confirmation.isConfirmedDate).format('MMM D, YYYY')})`}
                        </DetailValue>
                    </DetailItem>
                </TourDetails>
                {confirmation.isConfirmedByGuide === false && (
                    <ButtonGroup>
                        <ConfirmButton onClick={() => handleConfirmTour(tour._id, false, closeModal)}>
                            Reject Tour
                        </ConfirmButton>
                        <ConfirmButton $confirmed onClick={() => handleConfirmTour(tour._id, true, closeModal)}>
                            Confirm Tour
                        </ConfirmButton>
                    </ButtonGroup>
                )}
            </TourCard>
        );
    };

    if (!isAuthenticated || user?.role[0] !== Roles.GUIDE) {
        return <Navigate to="/" />;
    }

    if (loading) return <Loader />;

    const handleMonthChange = (date: Date) => {
        setCurrentMonth(date);
    };

    return (
        <Container>
            <GuideNav />
            <h2>Guide Dashboard</h2>

            <TourCalendar
                tours={guideToursForCalendar}
                renderTourCard={renderTourCard}
                onMonthChange={handleMonthChange}
                currentDate={currentMonth}
                showConfirmationLegend
            />

            {pendingTours.length > 0 && (
                <PendingToursSection>
                    <h3>Tours Pending Confirmation</h3>
                    {pendingTours.map((tour: any) => renderTourCard(tour))}
                </PendingToursSection>
            )}
            {loadingPendingTours && (
                <PendingToursSection>
                    <h3>Tours Pending Confirmation</h3>
                    <p>Loading pending tours from other months...</p>
                </PendingToursSection>
            )}
        </Container>
    );
};

export default GuideDashboard;
